import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, LogOut, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem('tranquil_admin_token') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [expanded, setExpanded] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBookings = async (authToken) => {
    setLoadingBookings(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings?limit=100`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
        return;
      }
      if (!res.ok) throw new Error('Failed to load bookings');
      const data = await res.json();
      // Sort by date desc, then time
      data.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setBookings(data);
    } catch (e) {
      toast.error('Failed to load bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchBookings(token);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginError(typeof data.detail === 'string' ? data.detail : 'Login failed');
        return;
      }
      localStorage.setItem('tranquil_admin_token', data.token);
      setToken(data.token);
      toast.success('Welcome back!');
    } catch (err) {
      setLoginError('Network error. Try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tranquil_admin_token');
    setToken('');
    setBookings([]);
    setEmail('');
    setPassword('');
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-accent-wash px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-light">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-accent-primary" size={28} />
            <h1 className="heading-3 m-0">Admin Login</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="body-small font-semibold block mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-light rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="body-small font-semibold block mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-light rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="admin-password-input"
              />
            </div>
            {loginError && (
              <p className="body-small text-red-600" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              className="btn-primary w-full"
              data-testid="admin-login-submit"
            >
              {loginLoading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            className="mt-6 body-small text-muted hover:text-primary block w-full text-center"
          >
            ← Back to site
          </button>
        </div>
      </div>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  const totalCount = bookings.length;
  const firstConsultCount = bookings.filter((b) => b.is_first_consultation).length;

  return (
    <div className="min-h-screen bg-accent-wash py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 m-0">Bookings Dashboard</h1>
            <p className="body-small text-muted mt-1">
              {totalCount} total · {firstConsultCount} first consultation
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchBookings(token)}
              disabled={loadingBookings}
              className="btn-secondary flex items-center gap-2"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={16} className={loadingBookings ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {loadingBookings ? (
          <div className="text-center py-16 body-medium text-muted">Loading bookings…</div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 body-medium text-muted bg-white rounded-2xl shadow-sm">
            No bookings yet.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-light">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-bookings-table">
                <thead className="bg-accent-wash">
                  <tr>
                    <th className="px-4 py-3 body-small font-semibold">Date</th>
                    <th className="px-4 py-3 body-small font-semibold">Time</th>
                    <th className="px-4 py-3 body-small font-semibold">Service</th>
                    <th className="px-4 py-3 body-small font-semibold">Patient</th>
                    <th className="px-4 py-3 body-small font-semibold">Contact</th>
                    <th className="px-4 py-3 body-small font-semibold">Flags</th>
                    <th className="px-4 py-3 body-small font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <React.Fragment key={b.booking_id}>
                      <tr className="border-t border-light hover:bg-accent-wash/30">
                        <td className="px-4 py-3 body-small font-medium">{b.date}</td>
                        <td className="px-4 py-3 body-small">{b.time}</td>
                        <td className="px-4 py-3 body-small capitalize">{b.service}</td>
                        <td className="px-4 py-3 body-small">{b.name}</td>
                        <td className="px-4 py-3 body-small">
                          <div>{b.email}</div>
                          <div className="text-muted">{b.phone}</div>
                        </td>
                        <td className="px-4 py-3 body-small">
                          {b.is_first_consultation && (
                            <span className="inline-block bg-accent-primary/20 text-accent-primary px-2 py-0.5 rounded-full text-xs font-medium mr-1">
                              1st
                            </span>
                          )}
                          {b.is_at_home && (
                            <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              Home
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(b.is_first_consultation || b.reason_for_consultation) && (
                            <button
                              onClick={() => toggleExpand(b.booking_id)}
                              className="text-accent-primary hover:underline text-xs flex items-center gap-1"
                              data-testid={`admin-expand-${b.booking_id}`}
                            >
                              {expanded[b.booking_id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              Details
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded[b.booking_id] && (
                        <tr className="bg-accent-wash/50">
                          <td colSpan={7} className="px-6 py-4">
                            {b.reason_for_consultation && (
                              <div className="mb-3">
                                <div className="body-small font-semibold mb-1">Reason for Consultation</div>
                                <div className="body-small text-secondary">{b.reason_for_consultation}</div>
                              </div>
                            )}
                            {b.consultation_data && (
                              <div>
                                <div className="body-small font-semibold mb-1">First Consultation Intake</div>
                                <pre className="body-small bg-white border border-light rounded p-3 overflow-x-auto text-xs">
                                  {JSON.stringify(b.consultation_data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
